import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import axios from 'axios';
import JVP from './JVP';
import AnkleOedema from './AnkleOedema';
import ChestWall from './ChestWall';
import LungAuscultation from './LungAuscultation';
import SuccessModal from './SuccessModal';

const GeneralCheckupContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`;

const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0;
  gap: 12px;
  width: 100%;
`;

const SectionTitle = styled.h2`
  width: 100%;
  font-family: 'Sofia Sans', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  line-height: 17px;
  display: flex;
  align-items: center;
  color: #6D6D71;
`;

const SaveButton = styled.button`
  display: flex;
  width: 230px;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 15px 15px;
  gap: 12px;
  background: #2575BD;
  border: none;
  border-radius: 16px;
  font-family: 'Sofia Sans', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 18px;
  line-height: 22px;
  text-align: center;
  color: #FFFFFF;
  cursor: pointer;
  margin-top: 20px;

  &:hover {
    background-color: ${props => props.disabled ? '#E0F0FF' : '#1c5c94'};
  }
`;

const GeneralCheckup = ({ participantId, formData, onFormDataChange, onSaveSuccess }) => {
  const [data, setData] = useState({
    chest_wall: {
      Kyphosis: false,
      Scoliosis: false,
      Pectus_Excavatum: false,
      Pectus_Carinatum: false,
      Surgery_Sternotomy: false,
      Surgery_L_Thoracotomy: false,
      Surgery_R_Thoracotomy: false
    },
    jvp: {
      left: false,
      right: false
    },
    ankle_oedema: {
      left: 'none',
      right: 'none'
    },
    lung_auscultation: {
      left: {
        apex_anterior: [],
        base_posterior: [],
        axillary: []
      },
      right: {
        apex_anterior: [],
        base_posterior: [],
        axillary: []
      }
    }
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const fetchGeneralCheckup = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}participant/get-participant-info?participant_id=${participantId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.data) {
          const apiData = response.data;
          const initialData = {
            chest_wall: apiData.chest_wall || {
              Kyphosis: false,
              Scoliosis: false,
              Pectus_Excavatum: false,
              Pectus_Carinatum: false,
              Surgery_Sternotomy: false,
              Surgery_L_Thoracotomy: false,
              Surgery_R_Thoracotomy: false
            },
            jvp: apiData.jvp || {
              left: false,
              right: false
            },
            ankle_oedema: apiData.ankle_oedema || {
              left: 'none',
              right: 'none'
            },
            lung_auscultation: apiData.lung_auscultation || {
              left: {
                apex_anterior: [],
                base_posterior: [],
                axillary: []
              },
              right: {
                apex_anterior: [],
                base_posterior: [],
                axillary: []
              }
            }
          };
          
          setData(initialData);
          onFormDataChange(initialData);
        }
      } catch (error) {
        console.error('Error fetching general checkup data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchGeneralCheckup();
  }, [participantId]);

  const handleChange = (section, newData) => {
    const updatedData = {
      ...data,
      [section]: newData
    };
    setData(updatedData);
    onFormDataChange(updatedData);
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}participant/update-general`,
        { participant_id: participantId, ...data },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setIsModalOpen(true);
    } catch (error) {
      console.error('Error saving general checkup data:', error);
      // TODO: Implement error handling
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    onSaveSuccess();
  };

  if (isLoading) {
    return <div>Loading general checkup data...</div>;
  }

  return (
    <GeneralCheckupContainer>
      <SectionContainer>
        <SectionTitle>JUGULAR VENOUS PRESSURE (JVP)</SectionTitle>
        <JVP data={data.jvp} onChange={(newData) => handleChange('jvp', newData)} />
        <SectionTitle>ANKLE OEDEMA</SectionTitle>
        <AnkleOedema data={data.ankle_oedema} onChange={(newData) => handleChange('ankle_oedema', newData)} />
        <SectionTitle>CHEST WALL</SectionTitle>
        <ChestWall data={data.chest_wall} onChange={(newData) => handleChange('chest_wall', newData)} />
        <SectionTitle>LUNG AUSCULTATION</SectionTitle>
        <LungAuscultation data={data.lung_auscultation} onChange={(newData) => handleChange('lung_auscultation', newData)} />
      </SectionContainer>
      <SaveButton onClick={handleSave}>Save and Next</SaveButton>
      <SuccessModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        title="General Checkup Saved"
        message={`General checkup details have been successfully saved for Participant ID: ${participantId}`}
      />
    </GeneralCheckupContainer>
  );
};

export default GeneralCheckup;
